import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { clsx } from 'clsx'
import { errorMessage } from '@/shared/i18n/errorMessage'
import { useTranslation } from '@/shared/i18n/useTranslation'
import { useCompetitions } from '@/features/league/queries'
import { OptionCard } from '@/features/league/components/OptionCard'
import { useCreateSurvivorGame } from '../queries'
import styles from './SobreviventeCreatePage.module.css'

const MAX_NAME_LENGTH = 40

function competitionIcon(sport: string): string {
  return sport === 'FOOTBALL' ? '⚽' : '🖥️'
}

export function SobreviventeCreatePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: competitions, isPending: competitionsPending } = useCompetitions()
  const create = useCreateSurvivorGame()

  const [name, setName]                       = useState('')
  const [competitionId, setCompetitionId]     = useState<string>('')
  const [maxParticipants, setMaxParticipants] = useState<number>(100)
  const [nameError, setNameError]             = useState('')
  const [compError, setCompError]             = useState('')

  const nameLength = name.length
  const serverError: string = create.error
    ? errorMessage(create.error, t, 'error.survivor.create')
    : ''

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    let valid = true
    if (name.trim().length < 3) { setNameError('O nome deve ter pelo menos 3 caracteres.'); valid = false }
    else                         setNameError('')
    if (!competitionId)          { setCompError('Escolha uma competição.'); valid = false }
    else                          setCompError('')

    if (!valid) return

    create.mutate(
      { name: name.trim(), competitionId, maxParticipants },
      {
        onSuccess: (result) =>
          void navigate({ to: '/sobrevivente/$gameId', params: { gameId: result.gameId }, search: { gw: undefined } }),
      },
    )
  }

  function handleCancel() {
    void navigate({ to: '/sobrevivente' })
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageBg} aria-hidden="true" />

      <form
        className={styles.wizardWrap}
        onSubmit={handleSubmit}
        noValidate
      >
        <div className={styles.wizardCard}>
          {/* Header */}
          <div className={styles.cardHeader}>
            <span className={styles.stepLabel}>SOBREVIVENTE</span>
            <h1 className={styles.cardHeading}>Novo Jogo</h1>
          </div>

          {/* Body */}
          <div className={styles.cardBody}>

            {/* ── Name ─────────────────────────────────────────── */}
            <section className={styles.section}>
              <label htmlFor="sv-name" className={styles.sectionLabel}>
                NOME DO JOGO
              </label>
              <input
                id="sv-name"
                type="text"
                className={clsx(styles.nameInput, nameError && styles.nameInputError)}
                value={name}
                onChange={(e) => { setName(e.target.value); if (nameError) setNameError('') }}
                placeholder="Ex: Bolão dos Brabos"
                maxLength={MAX_NAME_LENGTH}
                autoComplete="off"
                autoFocus
                disabled={create.isPending}
                aria-invalid={!!nameError}
                aria-describedby={nameError ? 'sv-name-error' : undefined}
              />
              <div className={styles.nameFooterRow}>
                {nameError ? (
                  <p className={styles.fieldError} id="sv-name-error" role="alert">
                    {nameError}
                  </p>
                ) : (
                  <span />
                )}
                <span
                  className={clsx(
                    styles.nameCounter,
                    nameLength >= MAX_NAME_LENGTH - 5 && styles.nameCounterWarn,
                    nameLength >= MAX_NAME_LENGTH    && styles.nameCounterMax,
                  )}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {nameLength} / {MAX_NAME_LENGTH}
                </span>
              </div>
            </section>

            {/* ── Competition ──────────────────────────────────── */}
            <section className={styles.section}>
              <p className={styles.sectionLabel}>COMPETIÇÃO</p>
              {competitionsPending ? (
                <div className={styles.competitionSkeleton} aria-hidden="true" />
              ) : (
                <div role="radiogroup" aria-label="Competição" className={styles.optionGrid2}>
                  {competitions?.map((c) =>
                    c.isActive ? (
                      <OptionCard
                        key={c.id}
                        id={`competition-${c.id}`}
                        value={c.id}
                        title={c.name}
                        icon={competitionIcon(c.sport)}
                        eyebrow="DISPONÍVEL"
                        description="Competição disponível para novo jogo."
                        isSelected={competitionId === c.id}
                        onChange={(val) => { setCompetitionId(val); if (compError) setCompError('') }}
                      />
                    ) : (
                      <OptionCard
                        key={c.id}
                        id={`competition-${c.id}`}
                        value={c.id}
                        title={c.name}
                        icon={competitionIcon(c.sport)}
                        eyebrow="EM BREVE"
                        description="Em breve disponível."
                        isSelected={false}
                        isLocked
                      />
                    ),
                  )}
                </div>
              )}
              {compError && (
                <p className={styles.fieldError} role="alert">{compError}</p>
              )}
            </section>

            {/* ── Max participants ─────────────────────────────── */}
            <section className={styles.section}>
              <label htmlFor="sv-max" className={styles.sectionLabel}>
                MÁXIMO DE PARTICIPANTES
              </label>
              <input
                id="sv-max"
                type="number"
                className={styles.numberInput}
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(Math.max(2, Math.min(100, parseInt(e.target.value || '0', 10) || 0)))}
                min={2}
                max={100}
                disabled={create.isPending}
              />
            </section>

            <div className={styles.infoNote}>
              <span className={styles.infoNoteIcon} aria-hidden="true">ℹ</span>
              <p className={styles.infoNoteText}>
                Limite entre 2 e 100 participantes. Você poderá convidar amigos com o código
                gerado após criar o jogo.
              </p>
            </div>

            {serverError && (
              <div className={styles.formErrorAlert} role="alert">
                <p>{serverError}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={styles.cardFooter}>
            <button
              type="button"
              className={styles.btnBack}
              onClick={handleCancel}
              disabled={create.isPending}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={styles.btnNext}
              disabled={create.isPending}
            >
              {create.isPending ? 'Criando…' : 'Criar Jogo →'}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
